import type { NewsArticle, NewsType } from "@/types/news";
import { categories } from "@/mocks/news";

export function getCategoryName(id: string): string {
  const found = categories.find((c) => c.id === id);
  return found ? found.name : "综合";
}

/** Fixed reference time so demo filtering stays stable. */
export const NEWS_NOW = new Date("2026-10-14T12:00:00").getTime();

export function parseTime(value: string): number {
  return new Date(value.replace(" ", "T")).getTime();
}

export function formatViews(n: number): string {
  if (n >= 10000) {
    const v = (n / 10000).toFixed(1).replace(/\.0$/, "");
    return `${v}万`;
  }
  if (n >= 1000) {
    return `${(n / 1000).toFixed(1).replace(/\.0$/, "")}千`;
  }
  return String(n);
}

export function relativeTime(value: string): string {
  const diff = NEWS_NOW - parseTime(value);
  const hour = 60 * 60 * 1000;
  const day = 24 * hour;
  if (diff < hour) {
    const m = Math.max(1, Math.round(diff / (60 * 1000)));
    return `${m}分钟前`;
  }
  if (diff < day) {
    return `${Math.round(diff / hour)}小时前`;
  }
  if (diff < 7 * day) {
    return `${Math.round(diff / day)}天前`;
  }
  return value.slice(5, 10);
}

export const newsTypeLabels: Record<NewsType, string> = {
  text: "文字",
  image: "图片",
  video: "视频",
};

export interface NewsFilterState {
  time: "all" | "1d" | "3d" | "7d";
  type: NewsType | "all";
  region: string;
}

export type SortKey = "time" | "hot" | "comments";

export const defaultFilter: NewsFilterState = {
  time: "all",
  type: "all",
  region: "全部",
};

export function applyFilter(list: NewsArticle[], filter: NewsFilterState): NewsArticle[] {
  const day = 24 * 60 * 60 * 1000;
  return list.filter((item) => {
    if (filter.time !== "all") {
      const span = filter.time === "1d" ? day : filter.time === "3d" ? 3 * day : 7 * day;
      if (NEWS_NOW - parseTime(item.publishTime) > span) return false;
    }
    if (filter.type !== "all" && item.type !== filter.type) return false;
    if (filter.region !== "全部" && item.region !== filter.region) return false;
    return true;
  });
}

export function applySort(list: NewsArticle[], sort: SortKey): NewsArticle[] {
  const clone = [...list];
  if (sort === "time") {
    clone.sort((a, b) => parseTime(b.publishTime) - parseTime(a.publishTime));
  } else if (sort === "hot") {
    clone.sort((a, b) => b.views - a.views);
  } else {
    clone.sort((a, b) => b.comments - a.comments);
  }
  return clone;
}

export function searchArticles(list: NewsArticle[], keyword: string): NewsArticle[] {
  const kw = keyword.trim().toLowerCase();
  if (!kw) return [];
  return list.filter((item) => {
    const haystack = [item.title, item.summary, item.category, item.region, ...item.tags]
      .join(" ")
      .toLowerCase();
    return haystack.includes(kw);
  });
}

export function paginate<T>(list: T[], page: number, size: number): T[] {
  const start = (page - 1) * size;
  return list.slice(start, start + size);
}